import { Grid, MenuItem, TextField } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useContext, useState } from 'react';

import OverwatchModal from '@/components/OverwatchModal';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { type CompanyHoliday, type HolidayShiftType } from '@/Contexts/Settings/Context';

interface HolidayModalProps {
  handleClose: () => void;
  initialHoliday?: CompanyHoliday | null;
  onSave: (holiday: CompanyHoliday) => void;
  title?: string;
}

const SHIFT_OPTIONS: { value: HolidayShiftType; label: string }[] = [
  { value: 'whole_day', label: 'Whole Day' },
  { value: 'morning', label: 'Morning Shift' },
  { value: 'evening', label: 'Evening Shift' },
];

const HolidayModal: React.FC<HolidayModalProps> = ({ handleClose, initialHoliday, onSave, title }) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const [date, setDate] = useState<Dayjs | null>(initialHoliday?.date ? dayjs(initialHoliday.date) : dayjs());
  const [shift, setShift] = useState<HolidayShiftType>(initialHoliday?.shift || 'whole_day');
  const [description, setDescription] = useState<string>(initialHoliday?.description || '');

  const isFormInvalid = !date || !date.isValid() || description.trim() === '';

  const handleSave = () => {
    if (isFormInvalid || !date) return;

    onSave({
      date: date.format('YYYY-MM-DD'),
      shift,
      description: description.trim(),
    });
    handleClose();
  };

  return (
    <OverwatchModal
      title={title || (initialHoliday ? 'Edit Company Holiday' : 'Add Company Holiday')}
      size="sm"
      handleClose={handleClose}
      primaryButtonText="Save"
      primaryButtonAction={handleSave}
      disablePrimary={isFormInvalid}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
    >
      <Grid container spacing={2.5} sx={{ pt: 1 }}>
        <Grid size={12}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <DatePicker
              label="Holiday Date"
              value={date}
              onChange={(newDate) => setDate(newDate)}
              format={dateTimePreference.date}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !date || !date.isValid(),
                  helperText: !date || !date.isValid() ? 'Valid date is required' : '',
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        <Grid size={12}>
          <TextField
            select
            fullWidth
            label="Shift Applied To"
            value={shift}
            onChange={(e) => setShift(e.target.value as HolidayShiftType)}
          >
            {SHIFT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Description"
            placeholder="e.g. Bank Holiday, Christmas Day"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={description.trim() === ''}
            helperText={description.trim() === '' ? 'Description is required' : ''}
          />
        </Grid>
      </Grid>
    </OverwatchModal>
  );
};

export default HolidayModal;
